import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

import { parseDate2 } from '@/lib/utils/dateUtils';
import { store } from '@/store';

/**
 * Utility function to merge Tailwind CSS classes
 * Combines clsx for conditional classes and tailwind-merge for deduplication
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format currency values for display
 */
export function formatCurrency(amount: number, currency = 'INR') {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(amount);
}

/**
 * Format percentage values for display
 */
export function formatPercentage(percentage: number) {
  return `${percentage >= 0 ? '+' : ''}${percentage.toFixed(2)}%`;
}

/**
 * Format numbers with Indian number system (lakhs, crores)
 */
export function formatNumber(number: number) {
  return new Intl.NumberFormat('en-IN').format(number);
}

/**
 * Truncate text with ellipsis
 */
export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
}

/**
 * Debounce function for performance optimization
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number,
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

/**
 * Generate a unique ID
 */
export function generateId() {
  return Math.random().toString(36).substring(2) + Date.now().toString(36);
}

// utils/auth.ts - Updated to use Redux store
export const isAuthenticated = (): boolean => {
  // Get current authentication state from Redux store
  const state = store.getState();
  return state.user.session.isAuthenticated && !!state.user.session.authToken;
};

export const formatDate = (dateStr: string) => {
  if (!dateStr) return '-';
  // Use parseDate2 for consistent formatting
  return parseDate2(dateStr) || '-';
};

export const formatDateTime = (dateTimeStr: string) => {
  // Use parseDate2 for consistent date formatting, add time handling
  const formatted = parseDate2(dateTimeStr);
  if (!formatted) return dateTimeStr;

  const date = new Date(dateTimeStr);
  if (isNaN(date.getTime())) return dateTimeStr;

  const timeString = date.toLocaleTimeString('en-IN', { timeStyle: 'short' });
  return `${formatted} ${timeString}`;
};

export const formatDateUtil = (date: any) => {
  if (!date) return '-';
  return parseDate2(date.toString()) || '-';
};

// Utility to capitalize each word
export const capitalizeWords = (str: string | null | undefined) => {
  if (!str) return '';
  return str.replace(/\b\w/g, (char) => char.toUpperCase());
};

// Helper function to format date
export const formatDateModification = (date: any) => {
  if (!date) return '-';
  try {
    const formatted = parseDate2(date.toString());
    if (!formatted) return '-';

    const d = new Date(date);
    if (isNaN(d.getTime())) return '-';

    const timeString = d.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
    });

    return `${formatted} ${timeString}`;
  } catch (e) {
    return '-';
  }
};

export const formatIndianAmount = (value: number): string => {
  if (value >= 1e7) {
    return `₹${(value / 1e7).toFixed(1)} Cr`;
  } else if (value >= 1e5) {
    return `₹${(value / 1e5).toFixed(1)} L`;
  } else if (value >= 1e3) {
    return `₹${(value / 1e3).toFixed(0)} K`;
  } else {
    return `₹${value.toFixed(0)}`;
  }
};

export const dateFormat = (dateTimeStr: string) => {
  return parseDate2(dateTimeStr) || dateTimeStr;
};
